import React from 'react';
import { format } from 'date-fns';
import { ADMIN_COLORS, PARTIAL_PAYMENT_BADGE } from '../constants/bookingConst';
import { useConfig } from '../context/ConfigContext';
import { getCourtLabel } from '../utils/bookingUtils';
import { getBookingPaymentState } from '../utils/partialPaymentUtil';
import { formatMoney } from '../utils/currencyUtil';
import { isPackageBooking, getPackageBookingCourts, describePackageBookingTime } from '../utils/packageUtils';
import '../css/AdminBookingsList.css';
import { hasReceipt } from '../utils/receiptUtil';

// Shared jassen light palette (constants/bookingConst.js)
const COLORS = ADMIN_COLORS;

function AdminBookingsList({ allBookings, selectedDate, dateRange, neonLimeColor, searchQuery, setSearchQuery, currentPage, setCurrentPage, ITEMS_PER_PAGE, onBookingClick }) {
  // Needed for court display names (Config -> Sports & Courts).
  const { config } = useConfig();
  // Determine title based on whether dateRange is provided
  const getTitle = () => {
    if (dateRange) {
      const startStr = format(dateRange.startDate, 'MMM dd, yyyy');
      const endStr = format(dateRange.endDate, 'MMM dd, yyyy');
      return `Bookings from ${startStr} to ${endStr}`;
    }
    return `All Bookings for ${format(selectedDate, 'MMMM dd, yyyy')}`;
  };

  // Filter bookings based on search query
  const filteredBySearch = searchQuery
    ? allBookings.filter(booking => {
        const query = searchQuery.toLowerCase();

        // Search by confirmation ID
        if (booking.confirmationId?.toLowerCase().includes(query)) return true;

        // Search by user name
        if (booking.userName?.toLowerCase().includes(query)) return true;

        // Search by email
        if (booking.email?.toLowerCase().includes(query)) return true;

        // Search by phone
        if (booking.phone?.toLowerCase().includes(query)) return true;

        // Search by status
        if (booking.status?.toLowerCase().includes(query)) return true;

        // Search by package name — an admin looking for an event knows it as
        // "Birthday Package", not by the court-hours it happens to occupy.
        if (booking.packageName?.toLowerCase().includes(query)) return true;

        // Search by date — checks every date in the group (see
        // groupBookingRecords), not just the primary/first one.
        const dateGroups = booking.dates && booking.dates.length > 0 ? booking.dates : [{ date: booking.date, timeSlots: booking.timeSlots }];
        if (dateGroups.some(g => g.date?.includes(query))) return true;

        // Search by time slots, across every date in the group.
        const timeSlotsStr = dateGroups.flatMap(g => Object.keys(g.timeSlots || {})).join(' ').toLowerCase();
        if (timeSlotsStr.includes(query)) return true;

        return false;
      })
    : allBookings;

  // Pagination
  const totalPages = Math.ceil(filteredBySearch.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedBookings = filteredBySearch.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <div className="bookings-list">
      <h3>{getTitle()}</h3>

      {/* Search Box */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by confirmation ID, name, email, time, or status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '0.5rem',
            borderRadius: '8px',
            border: `1px solid ${COLORS.line}`,
            background: COLORS.surface,
            color: COLORS.ink,
            fontSize: '0.85rem',
            width: '100%',
            maxWidth: '400px'
          }}
        />
        {searchQuery && (
          <span style={{ fontSize: '0.8rem', color: neonLimeColor }}>
            {filteredBySearch.length} result{filteredBySearch.length !== 1 ? 's' : ''} found
          </span>
        )}
      </div>

      {filteredBySearch.length === 0 ? (
        <p className="placeholder-text">No bookings found</p>
      ) : (
        <div className="bookings-table-wrapper">
          <table className="bookings-table">
            <thead>
              <tr>
                <th>Confirmation Id</th>
                <th>User Details</th>
                <th>Date</th>
                <th>Reserved Slots</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.map(booking => {
                const isInactive = booking.status === 'cancelled' || booking.status === 'disapproved';
                // "Not partial" for every booking that predates
                // downpayments, so this column is unchanged for them.
                const paymentState = getBookingPaymentState(booking);

                // A multi-date booking's `dates` array (see
                // groupBookingRecords) covers every date it reserves — just
                // one, for an ordinary single-date booking. Slots are
                // prefixed with their date once there's more than one, so
                // rows stay unambiguous.
                const dateGroups = booking.dates && booking.dates.length > 0 ? booking.dates : [{ date: booking.date, timeSlots: booking.timeSlots }];
                const isMultiDate = dateGroups.length > 1;
                const targetSlots = [];
                if (isPackageBooking(booking)) {
                  // One block, not a run of court-hours: a 4-hour package on
                  // two courts would otherwise fill this cell with ten pills
                  // — including the cleanup hour, which was never sold.
                  targetSlots.push(`${booking.packageName || 'Package'} · ${describePackageBookingTime(booking)}`);
                  const courts = getPackageBookingCourts(booking)
                    .map(court => getCourtLabel(config, court))
                    .join(', ');
                  if (courts) targetSlots.push(courts);
                } else {
                  dateGroups.forEach(group => {
                    Object.entries(group.timeSlots || {}).forEach(([timeSlot, courts]) => {
                      const courtList = Array.isArray(courts) ? courts : Object.keys(courts).map(Number);
                      courtList.forEach(court => {
                        const prefix = isMultiDate && group.date ? `${group.date} — ` : '';
                        targetSlots.push(`${prefix}${timeSlot} (${getCourtLabel(config, court)})`);
                      });
                    });
                  });
                }

                return (
                  <tr
                    key={booking.id}
                    style={{
                      borderBottom: `1px solid ${COLORS.line}`,
                      opacity: isInactive ? 0.5 : 1,
                      textDecoration: booking.status === 'cancelled' ? 'line-through' : 'none'
                    }}
                  >
                    {/* ID Column - Clickable */}
                    <td
                      onClick={() => onBookingClick && onBookingClick(booking)}
                      style={{
                        fontWeight: 'bold',
                        color: isInactive ? COLORS.inkFaint : neonLimeColor,
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      title="Click to manage booking"
                    >
                      {booking.confirmationId}
                    </td>

                    {/* User Info Column */}
                    <td>
                      <div style={{ fontWeight: '500', color: COLORS.ink }}>{booking.userName}</div>
                      <div style={{ fontSize: '12px', color: COLORS.inkFaint }}>{booking.email}</div>
                      <div style={{ fontSize: '12px', color: COLORS.inkFaint }}>{booking.phone}</div>
                    </td>

                    {/* Date Column */}
                    <td style={{ fontSize: '13px' }}>
                      {isMultiDate
                        ? `${format(new Date(dateGroups[0].date), 'MMM dd, yyyy')} (+${dateGroups.length - 1} more)`
                        : (dateGroups[0]?.date ? format(new Date(dateGroups[0].date), 'MMM dd, yyyy') : '-')}
                    </td>

                    {/* Grouped Time Slots & Courts */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {targetSlots.map((slot, index) => (
                          <span key={index} className="slot-pill" style={{
                            background: COLORS.cardBg,
                            color: COLORS.ink,
                            padding: '2px 8px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            width: 'fit-content'
                          }}>
                            {slot}
                          </span>
                        ))}
                        {targetSlots.length === 0 && <span style={{ fontStyle: 'italic', fontSize: '12px', color: COLORS.inkFaint }}>No slots assigned</span>}
                      </div>
                    </td>
                    
                    {/* Status Column */}
                    <td>
                      <span className={`status-badge ${booking.status}`}>
                        {booking.status.toUpperCase()}
                        {booking.status === 'pending' && hasReceipt(booking) && ' 📎'}
                      </span>
                      {/* A SECOND badge rather than a replacement for the
                          first: a partially-paid reservation is genuinely
                          confirmed, it just has not been paid off, and both
                          facts matter when the guest reaches the counter. */}
                      {paymentState.isPartial && (
                        <span
                          className="status-badge partial"
                          title={`Balance due: ${formatMoney(paymentState.balanceDue, config)}`}
                        >
                          {PARTIAL_PAYMENT_BADGE}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '0.4rem 0.6rem',
              background: COLORS.surface,
              color: currentPage === 1 ? COLORS.inkFaint : COLORS.ink,
              border: `1px solid ${COLORS.line}`,
              borderRadius: '8px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem'
            }}
          >
            «« First
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '0.4rem 0.6rem',
              background: COLORS.surface,
              color: currentPage === 1 ? COLORS.inkFaint : COLORS.ink,
              border: `1px solid ${COLORS.line}`,
              borderRadius: '8px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem'
            }}
          >
            « Prev
          </button>
          <span style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', color: neonLimeColor, fontWeight: 600 }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '0.4rem 0.6rem',
              background: COLORS.surface,
              color: currentPage === totalPages ? COLORS.inkFaint : COLORS.ink,
              border: `1px solid ${COLORS.line}`,
              borderRadius: '8px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem'
            }}
          >
            Next »
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '0.4rem 0.6rem',
              background: COLORS.surface,
              color: currentPage === totalPages ? COLORS.inkFaint : COLORS.ink,
              border: `1px solid ${COLORS.line}`,
              borderRadius: '8px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem'
            }}
          >
            Last »»
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminBookingsList;